import type { Question } from '../types';
import { OFICIALES_TOTAL, OPEN_QUESTIONS } from './official-open';

/**
 * El banco crece en tres tramos, y cada uno se gana de una forma:
 *
 * 1. **Abierto**, y es lo único que viaja en el bundle: 20 muestras oficiales
 *    por módulo, 80 en total.
 * 2. **Con cuenta**: las 107 oficiales restantes, que sirve el servidor en
 *    `/api/questions/oficiales` con la sesión iniciada y el correo verificado.
 * 3. **Con licencia**: las 581 propias y las explicaciones razonadas, en
 *    `/api/questions/premium`.
 *
 * `src/data/official.ts` mantiene las 187 completas, pero ya no lo importa el
 * cliente: lo usan las herramientas del build y las páginas estáticas.
 */
const bank: Question[] = [...OPEN_QUESTIONS];

/** Preguntas practicables sin cuenta. */
export const OPEN_COUNT = OPEN_QUESTIONS.length;

/** Las 187 oficiales, que se abren enteras al verificar el correo. */
export { OFICIALES_TOTAL };

let conCuenta = 0;
let dePago = 0;

export function getQuestions(): Question[] {
  return bank;
}

/** Añade preguntas nuevas al banco, descartando las que ya estaban. */
function añadir(questions: Question[]): number {
  const known = new Set(bank.map((q) => q.id));
  const nuevas = questions.filter((q) => q && !known.has(q.id));
  bank.push(...nuevas);
  return nuevas.length;
}

/** Incorpora las oficiales que exigen cuenta. */
export function addOficiales(questions: Question[]): number {
  const nuevas = añadir(questions);
  conCuenta += nuevas;
  return nuevas;
}

/** Incorpora el banco de pago descargado. */
export function addPremium(questions: Question[]): number {
  const nuevas = añadir(questions);
  dePago += nuevas;
  return nuevas;
}

/**
 * Pega las explicaciones que llegan del servidor sobre las preguntas del banco.
 * Las muestras oficiales vienen sin `explain`: este es el único sitio donde lo
 * reciben, y por eso tampoco están en el bundle.
 */
export function applyExplains(explains: Record<string, string>): number {
  let puestas = 0;
  for (const q of bank) {
    const texto = explains[q.id];
    if (texto && !q.explain) {
      q.explain = texto;
      puestas++;
    }
  }
  return puestas;
}

export const hasOficiales = (): boolean => conCuenta > 0;
export const hasPremium = (): boolean => dePago > 0;

/** Cuántas preguntas hay disponibles ahora mismo, por tramo. */
export const bankStats = () => ({ abiertas: OPEN_COUNT, conCuenta, dePago, total: bank.length });
